import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Monitor, MapPin, Calendar, CheckCircle } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';

const SessionsPage = ({ lang }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(lang);
    const profile_url = process.env.REACT_APP_API_PROFILE_URL;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessionsData();
  }, []);

  const fetchSessionsData = async () => {
    try {
      const response = await api.get(profile_url);

      const data = await response.data;
      
      
      if (data.result && data.result.sessionLog) {
        const formattedSessions = data.result.sessionLog.map((log, index) => ({
          id: index + 1,
          status: log.status || 'unknown',
          ip: log.ip || '',
          message: log.message || '',
          date: log.date || '',
          // La première session est la plus récente
          isCurrent: index === 0
        }));
        
        setSessions(formattedSessions);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // Retourne la date telle quelle, ou une version formatée si possible
    if (!dateString) return 'Date inconnue';
    
    // Si la date est déjà dans un bon format (07/01/2026 07:18), on la retourne
    return dateString;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'success': return 'Connexion réussie';
      case 'error': return 'Échec de connexion';
      default: return 'Statut inconnu';
    }
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '32px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      cursor: 'pointer',
      color: '#374151',
      marginBottom: '16px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    sessionCard: {
      background: '#f8fafc',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      marginBottom: '16px',
      position: 'relative'
    },
    currentSessionBadge: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 8px',
      background: '#10b981',
      color: 'white',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600'
    },
    sessionDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    sessionDetail: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#6b7280'
    },
    ipAddress: {
      fontFamily: 'monospace',
      backgroundColor: '#f3f4f6',
      padding: '2px 6px',
      borderRadius: '4px'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      marginTop: '8px'
    },
    noSessions: {
      textAlign: 'center',
      padding: '40px',
      color: '#6b7280',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '2px dashed #d1d5db'
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '40px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button 
            style={styles.backButton}
            onClick={() => navigate('/settings')}
            onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.background = 'white'}
          >
            <ArrowLeft size={20} />
            {t('backToSettings')}
          </button>
          
          <div style={styles.titleContainer}>
            <Monitor size={32} color="#7c3aed" />
            <h1 style={styles.title}>{t('sessions')}</h1>
          </div>
        </div>
        
        <div style={styles.loadingContainer}>
        <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button 
          style={styles.backButton}
          onClick={() => navigate('/settings')}
          onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
          onMouseLeave={(e) => e.target.style.background = 'white'}
        >
          <ArrowLeft size={20} />
          {t('backToSettings')}
        </button>
        
        <div style={styles.titleContainer}>
          <Monitor size={32} color="#7c3aed" />
          <h1 style={styles.title}>{t('sessions')}</h1>
        </div>
      </div>

      {/* Historique des Connexions */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Monitor size={24} />
          {t('connectionHistory')} ({sessions.length})
        </h2>

        {sessions.length > 0 ? (
          <>
            {sessions.map((session) => (
              <div key={session.id} style={styles.sessionCard}>
                {session.isCurrent && (
                  <div style={styles.currentSessionBadge}>
                    <CheckCircle size={14} />
                    {t('lastConnection')}
                  </div>
                )}
                
                <div style={styles.sessionDetails}>
                  <div style={styles.sessionDetail}>
                    <MapPin size={16} />
                    <span>{t('ipAddress')}: </span>
                    <span style={styles.ipAddress}>{session.ip}</span>
                  </div>
                  
                  <div style={styles.sessionDetail}>
                    <Calendar size={16} />
                    <span>{t('dateAndTime')}: </span>
                    <span>{formatDate(session.date)}</span>
                  </div>
                  
                  <div style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(session.status) + '20',
                    color: getStatusColor(session.status)
                  }}>
                    {session.status === 'success' && <CheckCircle size={12} />}
                    {getStatusText(session.status)}
                  </div>
                  
                  {session.message && session.message !== 'The request is processed successfully.' && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                      {t('message')}: {session.message}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div style={styles.noSessions}>
            <Monitor size={48} />
            <p>{t('noConnectionHistory')}</p>
          </div>
        )}
      </div>

      {/* Informations de sécurité */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{t('securityInformation')}</h2>
        <div style={{ color: '#6b7280', lineHeight: '1.6' }}>
          <p>{t('sessionsSecurityInfo1')}</p>
          <p>{t('sessionsSecurityInfo2')}</p>
          <p>{t('sessionsSecurityInfo3')}</p>
        </div>
      </div>
    </div>
  );
};

export default SessionsPage;